import React, { useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { 
  Cable, ClipboardCheck, FileText, Camera, Ruler, Shield, Users, Building, 
  Home as HomeIcon, CheckCircle2, XCircle, AlertCircle, Smartphone, Lock,
  TrendingUp, Zap, MapPin, Settings, BarChart3, Download, Eye, Clock,
  Award, Target, ArrowRight, Check, X, Mail, Phone, Sparkles, Menu
} from 'lucide-react';
import { submitDemoRequest, submitPilotApplication } from '../services/api';
import {
  FadeInWhenVisible, StaggerContainer, StaggerItem, TiltCard,
  AnimatedGrid, FloatingParticles, MagneticButton, ScrollProgress, StatCounter
} from '../components/AnimatedUI';

const Home = () => {
  const { toast } = useToast();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [demoForm, setDemoForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [pilotForm, setPilotForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });

  const handleDemoSubmit = (e) => {
    e.preventDefault();
    submitDemoRequest(demoForm)
      .then(() => {
        toast({ title: "✓ Demo-Anfrage erhalten!", description: "Wir melden uns in Kürze bei Ihnen." });
        setDemoForm({ name: '', company: '', email: '', phone: '', message: '' });
      })
      .catch(() => toast({ title: "Fehler", description: "Bitte versuchen Sie es später erneut.", variant: "destructive" }));
  };

  const handlePilotSubmit = (e) => {
    e.preventDefault();
    submitPilotApplication(pilotForm)
      .then(() => {
        toast({ title: "✓ Pilotanfrage erhalten!", description: "Wir melden uns in Kürze bei Ihnen." });
        setPilotForm({ name: '', company: '', email: '', phone: '', message: '' });
      })
      .catch(() => toast({ title: "Fehler", description: "Bitte versuchen Sie es später erneut.", variant: "destructive" }));
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <ScrollProgress />

      {/* Header */}
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="fixed top-0 w-full bg-white/80 backdrop-blur-xl z-50 border-b border-gray-200/50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.a 
              href="#"
              className="flex items-baseline cursor-pointer select-none"
              whileHover={{ scale: 1.03 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <span className="text-2xl font-bold tracking-tight text-gray-900">
                Anschluss
              </span>
              <span className="text-2xl font-bold tracking-tight bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                Pilot
              </span>
              <span className="text-2xl font-bold text-cyan-500 ml-0.5">.</span>
            </motion.a>
            <nav className="hidden md:flex space-x-8">
              {['Funktionen', 'Workflow', 'Preise', 'FAQ'].map((item, idx) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="text-gray-600 hover:text-cyan-600 transition-colors relative group"
                  whileHover={{ y: -2 }}
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 group-hover:w-full transition-all duration-300" />
                </motion.a>
              ))}
            </nav>
            <MagneticButton>
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white shadow-lg shadow-cyan-500/30">
                <a href="#demo">Demo anfragen</a>
              </Button>
            </MagneticButton>
          </div>
        </div>
      </motion.header>

      {/* Hero Section - Ultra Modern */}
      <section ref={heroRef} className="relative min-h-screen pt-24 pb-20 overflow-hidden bg-gray-950">
        {/* Animated Background */}
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ y: heroY, opacity: heroOpacity }}
        >
          <img 
            src="https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxmaWJlciUyMG9wdGljJTIwdGVjaG5vbG9neXxlbnwwfHx8fDE3ODAyNTQ1MzJ8MA&ixlib=rb-4.1.0&q=85"
            alt="Background"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/90 to-gray-950" />
        </motion.div>
        
        <AnimatedGrid />
        <FloatingParticles count={30} />

        {/* Animated glow orbs */}
        <motion.div 
          className="absolute top-20 -left-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-[120px]"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-20 -right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-[120px]"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.6, 0.3, 0.6] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Badge className="mb-6 bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 backdrop-blur-sm">
                  <Sparkles className="h-3 w-3 mr-2" />
                  MVP-Phase · Pilotkunden gesucht
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-5xl lg:text-7xl font-bold mb-6 leading-[1.05] tracking-tight"
              >
                Die mobile Field-App für{' '}
                <span className="bg-gradient-to-r from-cyan-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                  <TypeAnimation
                    sequence={[
                      'Glasfaser-Hausanschlüsse',
                      2000,
                      'NE3-Projekte',
                      2000,
                      'NE4-Montagen',
                      2000,
                      'PDF-Dokumentation',
                      2000,
                    ]}
                    wrapper="span"
                    speed={50}
                    repeat={Infinity}
                  />
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl mb-8 text-gray-300 leading-relaxed max-w-xl"
              >
                AnschlussPilot digitalisiert NE3- und NE4-Prozesse – von der Begehung über Pflichtfotos, Aufmaß und Messung bis zur Abnahme und PDF-Dokumentation.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 mb-12"
              >
                <MagneticButton>
                  <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white text-lg px-8 shadow-2xl shadow-cyan-500/40 group">
                    <a href="#demo" className="flex items-center">
                      Demo anfragen 
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </MagneticButton>
                <MagneticButton>
                  <Button size="lg" variant="outline" className="bg-white/5 border-white/20 text-white hover:bg-white/10 backdrop-blur-md text-lg px-8">
                    <a href="#pilot">Pilotprojekt starten</a>
                  </Button>
                </MagneticButton>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10"
              >
                <StatCounter value="100" suffix="%" label="Pflichtdoku" />
                <StatCounter value="9" suffix="+" label="PDF-Typen" />
                <StatCounter value="2" suffix="+" label="Auftragstypen" />
              </motion.div>
            </div>

            {/* iPhone Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative flex justify-center"
            >
              <div className="relative">
                {/* iPhone Frame */}
                <motion.div
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="relative"
                >
                  <div className="relative w-[300px] h-[600px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-[40px] p-3 shadow-2xl shadow-cyan-500/30 border border-white/10">
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/30 to-blue-500/30 rounded-[40px] blur-2xl -z-10" />
                    
                    {/* Notch */}
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-10" />
                    
                    {/* Screen */}
                    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-white rounded-[32px] overflow-hidden relative">
                      {/* Status bar */}
                      <div className="h-12 flex items-center justify-between px-6 pt-2 text-xs font-semibold">
                        <span>9:41</span>
                        <span className="flex items-center space-x-1">
                          <span>●●●</span>
                        </span>
                      </div>
                      
                      {/* App Header */}
                      <div className="px-5 py-3">
                        <div className="text-xs text-gray-500 mb-1">Heute, 14:32</div>
                        <h3 className="text-lg font-bold text-gray-900">Cockpit</h3>
                      </div>

                      {/* Progress card */}
                      <div className="mx-5 mb-3 p-4 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl text-white">
                        <div className="text-xs opacity-80 mb-1">Fortschritt</div>
                        <div className="text-3xl font-bold mb-2">78%</div>
                        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: '78%' }}
                            transition={{ duration: 2, delay: 1 }}
                            className="h-full bg-white rounded-full"
                          />
                        </div>
                      </div>

                      {/* Stats grid */}
                      <div className="grid grid-cols-2 gap-2 px-5 mb-3">
                        {[
                          { label: 'Abgenommen', value: '142', color: 'text-green-600', bg: 'bg-green-50' },
                          { label: 'Offen', value: '14', color: 'text-orange-500', bg: 'bg-orange-50' },
                          { label: 'Fotos fehlen', value: '8', color: 'text-red-500', bg: 'bg-red-50' },
                          { label: 'Nacharbeit', value: '6', color: 'text-amber-600', bg: 'bg-amber-50' },
                        ].map((stat, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1 + i * 0.1 }}
                            className={`${stat.bg} rounded-xl p-3`}
                          >
                            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                            <div className="text-xs text-gray-600">{stat.label}</div>
                          </motion.div>
                        ))}
                      </div>

                      {/* Task list */}
                      <div className="px-5">
                        <div className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wider">Heute</div>
                        {[
                          { name: 'Musterstr. 12, WHG 3', status: 'done', icon: CheckCircle2 },
                          { name: 'Hauptstr. 45, WHG 1', status: 'progress', icon: Clock },
                          { name: 'Bahnhofstr. 8, NE3', status: 'pending', icon: AlertCircle },
                        ].map((task, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 1.5 + i * 0.15 }}
                            className="flex items-center py-2.5 border-b border-gray-100"
                          >
                            <task.icon className={`h-5 w-5 mr-3 ${
                              task.status === 'done' ? 'text-green-500' :
                              task.status === 'progress' ? 'text-cyan-500' : 'text-orange-500'
                            }`} />
                            <span className="text-sm text-gray-800 flex-1">{task.name}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating badges around phone */}
                <motion.div
                  animate={{ y: [0, -10, 0], rotate: [0, 3, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                  className="absolute -top-4 -left-12 bg-white/95 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-cyan-200"
                >
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm font-semibold text-gray-900">Live Sync</span>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0], rotate: [0, -3, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
                  className="absolute -bottom-4 -right-8 bg-white/95 backdrop-blur-md rounded-2xl px-4 py-3 shadow-xl border border-cyan-200"
                >
                  <div className="flex items-center space-x-2">
                    <Camera className="h-4 w-4 text-cyan-500" />
                    <span className="text-sm font-semibold text-gray-900">142 Fotos</span>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 4, repeat: Infinity, delay: 2 }}
                  className="absolute top-1/3 -right-16 bg-gradient-to-br from-green-500 to-emerald-500 text-white rounded-2xl px-4 py-3 shadow-xl shadow-green-500/40"
                >
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-sm font-semibold">Abgenommen</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Trust text */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-center text-gray-400 mt-16 text-sm tracking-wide"
          >
            FÜR AUFTRAGNEHMER · MONTEURE · BAULEITER · AUFTRAGGEBER IM GLASFASERAUSBAU
          </motion.p>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1 h-2 bg-white/60 rounded-full" />
          </motion.div>
        </motion.div>
      </section>

      {/* All other sections imported */}
      <ModernSections 
        demoForm={demoForm}
        setDemoForm={setDemoForm}
        pilotForm={pilotForm}
        setPilotForm={setPilotForm}
        handleDemoSubmit={handleDemoSubmit}
        handlePilotSubmit={handlePilotSubmit}
        roiCalculator={<ROICalculator />}
      />
    </div>
  );
};

// Import the rest of the sections
import ModernSections from './ModernSections';
import ROICalculator from '../components/ROICalculator';

export default Home;
